import React, { useRef, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CodeEditor = ({ 
  code, 
  onChange, 
  language, 
  onLanguageChange, 
  onRun, 
  isRunning, 
  autoSave, 
  onToggleAutoSave 
}) => {
  const editorRef = useRef(null);
  const [lineCount, setLineCount] = useState(1);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, column: 1 });

  const languageOptions = [
    { value: 'python', label: 'Python', description: 'Selenium with Python' },
    { value: 'javascript', label: 'JavaScript', description: 'Selenium with Node.js' },
    { value: 'java', label: 'Java', description: 'Selenium with Java' }
  ];

  useEffect(() => {
    const lines = code?.split('\n')?.length;
    setLineCount(lines);
  }, [code]);

  const handleTextareaChange = (e) => {
    const value = e?.target?.value;
    onChange(value);
    
    // Update cursor position
    const textarea = e?.target;
    const lines = value?.substring(0, textarea?.selectionStart)?.split('\n');
    setCursorPosition({
      line: lines?.length,
      column: lines?.[lines?.length - 1]?.length + 1
    });
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Tab') {
      e?.preventDefault();
      const textarea = e?.target;
      const start = textarea?.selectionStart;
      const end = textarea?.selectionEnd;
      const value = textarea?.value;
      
      // Insert 4 spaces for tab
      const newValue = value?.substring(0, start) + '    ' + value?.substring(end);
      onChange(newValue);
      
      // Set cursor position after the inserted spaces
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 4;
      }, 0);
    }
  };

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Editor Header */}
      <div className="flex items-center justify-between p-3 border-b border-subtle">
        <div className="flex items-center space-x-3">
          <Select
            options={languageOptions}
            value={language}
            onChange={onLanguageChange}
            className="w-40"
          />
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              iconName={autoSave ? "Check" : "Save"}
              iconPosition="left"
              onClick={onToggleAutoSave}
              className={autoSave ? "text-success" : ""}
            >
              Auto-save
            </Button>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="text-xs text-muted-foreground">
            Ln {cursorPosition?.line}, Col {cursorPosition?.column}
          </div>
          <Button
            variant="default"
            size="sm"
            iconName="Play"
            iconPosition="left"
            onClick={onRun}
            loading={isRunning}
            disabled={isRunning}
          >
            {isRunning ? 'Running...' : 'Run Code'}
          </Button>
        </div>
      </div>
      {/* Editor Content */}
      <div className="flex-1 flex">
        {/* Line Numbers */}
        <div className="bg-muted/30 border-r border-subtle px-2 py-3 text-xs text-muted-foreground font-mono min-w-[50px]">
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i + 1} className="h-5 flex items-center justify-end pr-2">
              {i + 1}
            </div>
          ))}
        </div>

        {/* Code Input */}
        <div className="flex-1 relative">
          <textarea
            ref={editorRef}
            value={code}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            className="w-full h-full p-3 bg-transparent text-foreground font-mono text-sm resize-none outline-none"
            placeholder="# Write your Selenium automation code here...\nfrom selenium import webdriver\nfrom selenium.webdriver.common.by import By\n\n# Your code here"
            spellCheck={false}
          />
          
          {/* Syntax highlighting overlay would go here in a real implementation */}
          <div className="absolute top-3 right-3">
            {autoSave && (
              <div className="flex items-center space-x-1 text-xs text-success bg-success/10 px-2 py-1 rounded">
                <Icon name="Check" size={12} />
                <span>Saved</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Editor Footer */}
      <div className="flex items-center justify-between p-2 border-t border-subtle text-xs text-muted-foreground">
        <div className="flex items-center space-x-4">
          <span>UTF-8</span>
          <span>LF</span>
          <span>{language?.charAt(0)?.toUpperCase() + language?.slice(1)}</span>
        </div>
        <div className="flex items-center space-x-4">
          <span>{code?.length} characters</span>
          <span>{lineCount} lines</span>
        </div>
      </div>
    </div>
  );
};

export default CodeEditor;